import { Component, inject, input, output, computed, LOCALE_ID, model, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BundleListParams } from '../../../../../../pages/admin-dashboard/promotions/bundles-managment/bundles.model';
import { FilterSortingComponent } from '../../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterProductSearch } from '../../../../../../shared/ui/shared-filters/filter-product-search/filter-product-search';
import { FilterMultiSelectComponent } from '../../../../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';

@Component({
  selector: 'app-bundle-filters-component',
  imports: [CommonModule, FormsModule, FilterSortingComponent, FilterProductSearch, FilterMultiSelectComponent, FilterDateComponent],
  templateUrl: './bundle-filters-component.html',
  styleUrl: './bundle-filters-component.scss',
})
export class BundleFiltersComponent {
  private locale = inject(LOCALE_ID);

  filters = input.required<BundleListParams>();
  applyFilters = output<void>();
  resetFilters = output<void>();
  toggleSortOrder = output<void>();

  bundleSearchLoading = input<boolean>(false);
  bundleSearchError = input<string>('');
  bundleSearchResults = input<{ id: number; titleAr: string; titleEn: string }[]>([]);
  bundleSearchTerm = model<string>('');
  showBundleSearchDropdown = model<boolean>(false);
  bundleSearchChange = output<string>();
  bundleSelect = output<{ id: number; titleAr: string; titleEn: string }>();

  showStatusDropdown = signal(false);
  showDiscountDropdown = signal(false);

  isArabic() {
    return this.locale.startsWith('ar');
  }

  @HostListener('document:click')
  closeAllDropdowns() {
    this.showStatusDropdown.set(false);
    this.showDiscountDropdown.set(false);
    this.showBundleSearchDropdown.set(false);
  }

  onContainerClick(event: Event) {
    event.stopPropagation();
  }

  toggleStatus() {
    const current = this.showStatusDropdown();
    this.showStatusDropdown.set(!current);
    this.showDiscountDropdown.set(false);
  }

  toggleDiscount() {
    const current = this.showDiscountDropdown();
    this.showDiscountDropdown.set(!current);
    this.showStatusDropdown.set(false);
  }

  statusSelectedIds = computed(() =>
    (this.filters().status ?? []).map(String)
  );

  discountSelectedIds = computed(() =>
    this.filters().discountTypes ?? []
  );

  onStatusChange(ids: string[]) {
    const mapped = ids.map(Number).filter((n) => !isNaN(n));
    this.filters().status = mapped.length ? mapped : undefined;
  }

  onDiscountTypeChange(ids: string[]) {
    const valid = ids.filter((id): id is 'VALUE' | 'PERCENTAGE' => id === 'VALUE' || id === 'PERCENTAGE');
    this.filters().discountTypes = valid.length ? valid : undefined;
  }

  // Date validation
  startDateInvalid = computed(() => {
    const f = this.filters();
    return !!(f.startDateFrom && f.startDateTo && f.startDateFrom > f.startDateTo);
  });

  endDateInvalid = computed(() => {
    const f = this.filters();
    return !!(f.endDateFrom && f.endDateTo && f.endDateFrom > f.endDateTo);
  });

  onBundleSearchChange(value: string) {
    this.filters().title = value;
    this.bundleSearchChange.emit(value);
  }

  onBundleSelect(item: { id: number; titleAr: string; titleEn: string }) {
    this.filters().title = this.isArabic() ? item.titleAr : item.titleEn;
    this.bundleSelect.emit(item);
  }

  onBundleSearchFocus() {
    if (this.bundleSearchTerm()) {
      this.showBundleSearchDropdown.set(true);
    }
  }

  closeAllFiltersDropdowns() {
    this.showStatusDropdown.set(false);
    this.showDiscountDropdown.set(false);
    this.showBundleSearchDropdown.set(false);
  }

  onApply() {
    if (this.startDateInvalid() || this.endDateInvalid()) return;
    this.applyFilters.emit();
  }

  bundleSortOptions = computed(() => [
    { value: 'TITLE', labelEn: 'Bundle Name', labelAr: 'اسم الباقة' },
    { value: 'START_DATE', labelEn: 'Start Date', labelAr: 'تاريخ البداية' },
  ]);

  statusOptions = computed(() => [
    { id: '1', titleEn: 'Active', titleAr: 'نشط' },
    { id: '2', titleEn: 'Inactive', titleAr: 'غير نشط' },
    { id: '3', titleEn: 'Expired', titleAr: 'منتهي' },
  ]);

  discountTypeOptions = computed(() => [
    { id: 'PERCENTAGE', titleEn: 'Percentage', titleAr: 'نسبة مئوية' },
    { id: 'VALUE', titleEn: 'Fixed Amount', titleAr: 'مبلغ ثابت' },
  ]);
}